// packages/report-components/src/BrandContext.tsx
//
// The seam where a surface tells the components which brand the document
// wears (plan 2026-09-13, Besluit 9). Same shape as JumpContext: a provider
// the surface mounts, a hook the components read, null when there is none.
//
// Why a context and not the custom properties alone: `ChartFrame` hands
// ECharts literal colours, and the charts resolve those from
// `document.documentElement` — which the report editor's canvas shares with
// the host app. The canvas must not write brand tokens there (every
// dashboard would recolour), so the accent reaches the charts explicitly,
// through this context, on every surface alike.

import * as React from "react";

import { sanitizeBrand, type ReportBrandTokens } from "./brand";

const BrandContext = React.createContext<ReportBrandTokens | null>(null);

export type ReportBrandProviderProps = {
  brand: ReportBrandTokens | null | undefined;
  children: React.ReactNode;
};

export function ReportBrandProvider({ brand, children }: ReportBrandProviderProps): React.ReactElement {
  const value = React.useMemo(() => sanitizeBrand(brand), [brand]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

/** The document's brand (sanitized), or null on a surface that mounted no
 * provider — every report drawn before brands existed. */
export function useReportBrand(): ReportBrandTokens | null {
  return React.useContext(BrandContext);
}
